import React, { useEffect } from 'react';
import { Div, Typography } from '@icecreamsocial/components';
import { useHistory } from 'react-router-dom';
import { Offer, CodeUnlocker, PageWrapper } from '../../../../presentational';
import { useStore } from '../../../../hooks';
import { flags } from '../../../../../utils/featureFlags';
import { FormattedMessage, useIntl } from 'react-intl';
import PopsicleLoader from '../../../../presentational/PopsicleLoader';
import { observer } from 'mobx-react-lite';
import { usePostHog } from 'posthog-js/react';

/**
 * @featureflags
 */
const ENABLE_EXPERIMENTAL_SMS_CONSENT = flags.isActive(
	flags.names.ENABLE_EXPERIMENTAL_SMS_CONSENT
);

const ENABLE_EXPERIMENTAL_INCENTIVE_FLOW = flags.isActive(
	flags.names.ENABLE_EXPERIMENTAL_INCENTIVE_FLOW
);

export const OfferOptIn = observer(
	({
		email: defaultEmail,
		engagement,
		onEmailEntered,
		publicKey,
		children,
		mobileNumber,
		subtotal,
		...rest
	}) => {
		const history = useHistory();
		const intl = useIntl();
		const posthog = usePostHog();
		const {
			Campaign,
			View,
			Influencer,
			createInfluencer,
			trackConversion,
			type,
			getDiscountCode,
			setDiscountCode,
		} = useStore();

		const requireConsent = {
			email: Campaign?.useEmailConsent,
			sms: ENABLE_EXPERIMENTAL_SMS_CONSENT ? Campaign?.useSMSConsent : false, // do not require if not enabled
		};

		const onSubmit = async function({ email, emailConsent }) {
			const { id: campaignId } = Campaign || {};

			const SHOW_INCENTIVE =
				ENABLE_EXPERIMENTAL_INCENTIVE_FLOW && type === 'incentive';
			const finalDestination = SHOW_INCENTIVE ? '/offer-code' : '/share';

			await createInfluencer(campaignId, {
				orderId: Influencer.orderId,
				identifier: email,
				name: Influencer.name || email,
				revenue: subtotal,
				email,
				emailConsent,
			});
			await trackConversion({
				conversionType: 'participation',
				campaignId,
				email,
			});

			if (SHOW_INCENTIVE) {
				const { data } = await getDiscountCode(campaignId);
				setDiscountCode(data.discount?.discountCode);
			}

			posthog?.identify(Influencer?.id, Influencer);
			posthog?.capture('offer_opt_in');

			history.push(finalDestination);
		};

		/**
		 * localization strings
		 */
		const UNLOCK_INPUT = intl.formatMessage({
			id: 'unlock.input',
			defaultMessage: 'Your Email Address',
			description: 'Label for an input to enter an email',
		});

		const UNLOCK_LABEL = intl.formatMessage({
			id: 'unlock.label',
			defaultMessage: 'Activate Offer',
			description: 'Call to action to unlock offer',
		});

		// skip the email entry step
		useEffect(() => {
			// don't try anything
			if (requireConsent.email || requireConsent.sms) return;

			if (Campaign && defaultEmail)
				onSubmit({ email: defaultEmail, emailConsent: true });
		}, [Campaign, defaultEmail]);

		return (
			<PageWrapper>
				<Offer {...rest} />
				<Typography.P typoSize='sm'>
					<FormattedMessage
						id='optInMessage'
						defaultMessage={
							'To participate and share with your friends, let us know how to contact you with offer and reward updates'
						}
						description={
							'Prompts the user to tell us how to contact them for offer and reward updates'
						}
					/>
				</Typography.P>
				<Div paddingLeft='md' paddingRight='md'>
					{!View.isLoading.get('campaign') &&
					!View.isLoading.get('influencer') ? (
						<CodeUnlocker
							onSubmit={onSubmit}
							defaultEmail={defaultEmail}
							termsURL={Campaign?.termsAndConditionsURL}
							privacyURL={Campaign?.privacyPolicyURL}
							requireConsent={requireConsent}
							label={UNLOCK_INPUT}
							unlockLabel={UNLOCK_LABEL}
						/>
					) : (
						<Div
							padding='xlg'
							textAlign='center'
							backgroundColor='ambientLight'
							borderRadius='default'
							marginBottom='xlg'
						>
							<PopsicleLoader />
							<Typography.P marginTop='none'>
								Unlocking your offer...
							</Typography.P>
						</Div>
					)}
				</Div>
			</PageWrapper>
		);
	}
);
